import React, { useCallback, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { NotificationService, NOTIFICATION_DURATIONS, NOTIFICATION_TYPE_ERROR } from 'ui-components/services/notification-service';

import { useBinaryState } from '../../../../hooks/use-binary-state';
import { getImportMappings } from '../api';
import { messages } from './use-get-mapping-list-messages';

/**
 * @typedef {import("../types/types").ImportMapping} ImportMapping
 * @typedef {import("../types/types").UpdateMappingTemplatePayload} UpdateMappingTemplatePayload
 */

/**
 * @param {string} importType
 * @returns {{
 *  mappingList: ImportMapping[],
 *  getMappingById: (uniqueId: string) => ImportMapping | undefined,
 *  isMappingNameUnique: (mappingName: string) => boolean,
 *  updateMappingTemplate: (payload: UpdateMappingTemplatePayload) => void,
 *  deleteMappingTemplate: (mappingId: string) => void,
 *  isGetMappingsLoading: boolean,
 * }}
 */
export const useGetMappingList = (importType) => {
  const [mappingList, setMappingList] = useState(/** @type {ImportMapping[]} */ ([]));
  // you may wonder why is it set to true by default
  // it is because it fetches the data on component mount
  // and without true by default, the component instead of the spinner is displayed for a millisecond
  const [isGetMappingsLoading, startLoading, stopLoading] = useBinaryState(true);

  useEffect(() => {
    startLoading();
    getImportMappings(importType)
      .then((data) => {
        // Type assertion here because the API returns a different type
        const uniqueIdMappings = /** @type {ImportMapping[]} */ (data.mappings || []).map((i) => ({
          ...i,
          uniqueId: `${i.mappingId}|${i.name}`,
        }));
        setMappingList(uniqueIdMappings);
      })
      .catch(() => {
        NotificationService.showNotification({
          notificationType: NOTIFICATION_TYPE_ERROR,
          title: <FormattedMessage {...messages.errorTitle} />,
          content: <FormattedMessage {...messages.errorContent} />,
          duration: NOTIFICATION_DURATIONS.SHORT,
        });
      })
      .finally(stopLoading);
  }, [importType, startLoading, stopLoading]);

  /**
   * Returns the mapping object with the given uniqueId
   * @param {string} uniqueId
   * @returns {ImportMapping | undefined}
   */
  const getMappingById = (uniqueId) => {
    return mappingList.find((mapping) => mapping.uniqueId === uniqueId);
  };

  /**
   * Checks if the mapping name is unique in the mapping list
   * @param {string} mappingName
   * @returns {boolean}
   */
  const isMappingNameUnique = useCallback(
    (mappingName) => {
      if (!mappingName || !mappingList.length) {
        return true;
      }

      const prepareForMatching = (str) => str.trim().toLowerCase();

      const nameToMatch = prepareForMatching(mappingName);

      return !mappingList.some((mappingEl) => prepareForMatching(mappingEl.name) === nameToMatch);
    },
    [mappingList],
  );

  /**
   * Deletes the mapping template from the list based on the mappingId
   * @param {string} mappingId
   * @returns {void}
   */
  const deleteMappingTemplate = (mappingId) => {
    setMappingList(mappingList.filter((x) => x.mappingId !== mappingId));
  };

  /**
   * Updates the mapping template name in the list based on the mappingId
   * @param {UpdateMappingTemplatePayload} payload
   */
  const updateMappingTemplate = ({ mappingId, updatedName }) => {
    const mappings = [...mappingList];
    const index = mappingList.findIndex((mapping) => mapping.mappingId === mappingId);
    mappings[index].name = updatedName;
    setMappingList(mappings);
  };

  return {
    mappingList,
    getMappingById,
    isMappingNameUnique,
    updateMappingTemplate,
    deleteMappingTemplate,
    isGetMappingsLoading,
  };
};
